import React from 'react';
import { Helmet } from 'react-helmet';
import { useLanguage, getSlugForLanguage } from '../contexts/LanguageContext';
interface SEOSchemaProps {
  type?: 'home' | 'about' | 'projects' | 'products' | 'contact' | 'news' | 'article';
  title?: string;
  description?: string;
  breadcrumbs?: Array<{
    name: string;
    url: string;
  }>;
  article?: {
    headline: string;
    datePublished: string;
    dateModified: string;
    image: string;
    description: string;
  };
}
export function SEOSchema({
  type = 'home',
  title,
  description,
  breadcrumbs,
  article
}: SEOSchemaProps) {
  const {
    language
  } = useLanguage();
  const baseUrl = 'https://grahariaugemilang.com';
  const logoUrl = 'https://cdn.magicpatterns.com/uploads/sSNAfpvwR9DMLUnYLkRqa2/logo-GRG-Graha-Riau-Gemilang.png';
  const defaultImage = 'https://cdn.magicpatterns.com/uploads/9Xm28KaLFmnTEqcYBohmkM/PT.-Graha-riau-gemilang_MSDL.jpg';
  // Get current path and convert slug to both languages
  const getCurrentPath = () => {
    if (typeof window === 'undefined') return '/';
    const path = window.location.pathname;
    // Remove language prefix
    const pathWithoutLang = path.replace(/^\/(id|en)/, '');
    // Extract slug
    const slug = pathWithoutLang.replace('/', '');
    return slug || '/';
  };
  const currentSlug = getCurrentPath();
  // Generate language-specific URLs with proper slugs
  const idSlug = currentSlug !== '/' ? getSlugForLanguage(currentSlug, 'id') : '';
  const enSlug = currentSlug !== '/' ? getSlugForLanguage(currentSlug, 'en') : '';
  const idUrl = `${baseUrl}/id${idSlug ? `/${idSlug}` : ''}`;
  const enUrl = `${baseUrl}/en${enSlug ? `/${enSlug}` : ''}`;
  const currentUrl = language === 'id' ? idUrl : enUrl;
  const defaultUrl = `${baseUrl}${currentSlug !== '/' ? `/${idSlug}` : ''}`; // x-default points to Indonesian slug
  // Organization Schema (for all pages)
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${baseUrl}/#organization`,
    name: 'PT Graha Riau Gemilang',
    alternateName: 'Graha Riau Gemilang',
    url: baseUrl,
    logo: logoUrl,
    description: 'PT Graha Riau Gemilang is a trusted real estate developer in Riau, established in 2005, delivering quality residential and commercial properties.',
    email: 'mailto:grahariaugemilang@gmail.com',
    telephone: '+62-761-21493',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Jl. Tiung No. 16A, Kel. Kampung Melayu, Kec. Sukajadi',
      addressLocality: 'Pekanbaru',
      addressRegion: 'Riau',
      addressCountry: 'ID'
    },
    sameAs: ['https://www.instagram.com/pt.grahariaugemilang/'],
    memberOf: [{
      '@type': 'Organization',
      name: 'Real Estate Indonesia (REI)',
      identifier: '03.00174'
    }],
    contactPoint: [{
      '@type': 'ContactPoint',
      contactType: 'customer service',
      telephone: '+62-761-21493',
      email: 'grahariaugemilang@gmail.com',
      areaServed: 'ID',
      availableLanguage: ['id', 'en']
    }, {
      '@type': 'ContactPoint',
      contactType: 'sales',
      telephone: '+62-761-21493',
      email: 'grahariaugemilang@gmail.com',
      areaServed: 'ID',
      availableLanguage: ['id', 'en']
    }]
  };
  // Local Business Schema (for home and contact pages)
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'RealEstateAgent'],
    '@id': `${baseUrl}/#localbusiness`,
    name: 'PT Graha Riau Gemilang',
    url: baseUrl,
    image: [logoUrl],
    telephone: '+62-761-21493',
    email: 'mailto:grahariaugemilang@gmail.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Jl. Tiung No. 16A, Kel. Kampung Melayu, Kec. Sukajadi',
      addressLocality: 'Pekanbaru',
      addressRegion: 'Riau',
      addressCountry: 'ID'
    },
    areaServed: [{
      '@type': 'AdministrativeArea',
      name: 'Riau'
    }, {
      '@type': 'City',
      name: 'Pekanbaru'
    }, {
      '@type': 'AdministrativeArea',
      name: 'Kampar'
    }],
    priceRange: '$$',
    openingHoursSpecification: [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:00',
      closes: '17:00'
    }],
    sameAs: ['https://www.instagram.com/pt.grahariaugemilang/']
  };
  // Website Schema (for home page)
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${baseUrl}/#website`,
    url: baseUrl,
    name: 'PT Graha Riau Gemilang',
    publisher: {
      '@id': `${baseUrl}/#organization`
    },
    inLanguage: ['id', 'en']
  };
  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  } : null;
  // Article Schema (for news pages)
  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.headline,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: {
      '@type': 'Organization',
      name: 'PT Graha Riau Gemilang',
      '@id': `${baseUrl}/#organization`
    },
    publisher: {
      '@type': 'Organization',
      '@id': `${baseUrl}/#organization`,
      name: 'PT Graha Riau Gemilang',
      logo: {
        '@type': 'ImageObject',
        url: logoUrl
      }
    },
    image: [article.image],
    description: article.description
  } : null;
  return <Helmet data-id="element-230">
      {/* Organization Schema - Always included */}
      <script type="application/ld+json" data-id="element-231">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* Local Business Schema - Home and Contact pages */}
      {(type === 'home' || type === 'contact') && <script type="application/ld+json" data-id="element-232">
          {JSON.stringify(localBusinessSchema)}
        </script>}

      {/* Website Schema - Home page only */}
      {type === 'home' && <script type="application/ld+json" data-id="element-233">
          {JSON.stringify(websiteSchema)}
        </script>}

      {/* Breadcrumb Schema */}
      {breadcrumbSchema && <script type="application/ld+json" data-id="element-234">
          {JSON.stringify(breadcrumbSchema)}
        </script>}

      {/* Article Schema */}
      {articleSchema && <script type="application/ld+json" data-id="element-235">
          {JSON.stringify(articleSchema)}
        </script>}

      {/* Basic Meta Tags */}
      {title && <title data-id="element-236">{title}</title>}
      {description && <meta name="description" content={description} data-id="element-237" />}
      <meta name="keywords" content="properti riau, developer riau, rumah subsidi riau, rumah premium pekanbaru, properti komersial riau, graha riau gemilang, real estate riau, perumahan pekanbaru, investasi properti riau" data-id="element-238" />
      <meta name="author" content="PT Graha Riau Gemilang" data-id="element-239" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" data-id="element-240" />
      <meta name="googlebot" content="index, follow" data-id="element-241" />

      {/* Canonical URL - points to current language version */}
      <link rel="canonical" href={currentUrl} data-id="element-242" />

      {/* Hreflang Tags for Multi-language SEO with proper slugs */}
      <link rel="alternate" hrefLang="id" href={idUrl} data-id="element-243" />
      <link rel="alternate" hrefLang="en" href={enUrl} data-id="element-244" />
      <link rel="alternate" hrefLang="x-default" href={defaultUrl} data-id="element-245" />

      {/* Language Meta Tag */}
      <meta httpEquiv="content-language" content={language} data-id="element-246" />

      {/* Open Graph / Facebook Meta Tags */}
      {title && <meta property="og:title" content={title} data-id="element-247" />}
      {description && <meta property="og:description" content={description} data-id="element-248" />}
      <meta property="og:type" content="website" data-id="element-249" />
      <meta property="og:url" content={currentUrl} data-id="element-250" />
      <meta property="og:image" content={defaultImage} data-id="element-251" />
      <meta property="og:image:width" content="1200" data-id="element-252" />
      <meta property="og:image:height" content="630" data-id="element-253" />
      <meta property="og:image:alt" content="PT Graha Riau Gemilang - Developer Properti Terpercaya di Riau" data-id="element-254" />
      <meta property="og:site_name" content="PT Graha Riau Gemilang" data-id="element-255" />
      <meta property="og:locale" content={language === 'id' ? 'id_ID' : 'en_US'} data-id="element-256" />
      <meta property="og:locale:alternate" content={language === 'id' ? 'en_US' : 'id_ID'} data-id="element-257" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" data-id="element-258" />
      {title && <meta name="twitter:title" content={title} data-id="element-259" />}
      {description && <meta name="twitter:description" content={description} data-id="element-260" />}
      <meta name="twitter:image" content={defaultImage} data-id="element-261" />
      <meta name="twitter:image:alt" content="PT Graha Riau Gemilang - Developer Properti Terpercaya di Riau" data-id="element-262" />
      <meta name="twitter:site" content="@grahariaugemilang" data-id="element-263" />
      <meta name="twitter:creator" content="@grahariaugemilang" data-id="element-264" />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#C4161C" data-id="element-265" />
      <meta name="msapplication-TileColor" content="#C4161C" data-id="element-266" />
      <meta name="apple-mobile-web-app-capable" content="yes" data-id="element-267" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" data-id="element-268" />
      <meta name="format-detection" content="telephone=yes" data-id="element-269" />
      <meta name="geo.region" content="ID-RI" data-id="element-270" />
      <meta name="geo.placename" content="Pekanbaru" data-id="element-271" />
      <meta name="geo.position" content="0.5347;101.4347" data-id="element-272" />
      <meta name="ICBM" content="0.5347, 101.4347" data-id="element-273" />
    </Helmet>;
}